/**
 * HtmlCardHost
 *
 * Sandboxed iframe host for Muse-style HTML cards in chat messages.
 * Injects theme CSS variables + state bridge (getState / setState).
 */

'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { SuggestedResponses } from '@/components/chat/suggested-responses'
import {
  HTML_CARD_HOST_SOURCE,
  HTML_CARD_MAX_BYTES,
  buildHtmlCardBridgeRuntime,
  clampHtmlCardFallback,
  isHtmlCardToHostMessage,
  measureHtmlCardBytes,
  type HtmlCardState
} from '@/lib/chat/html-card/bridge'
import {
  buildHtmlCardBaseStyles,
  resolveHtmlCardTheme
} from '@/lib/chat/html-card/theme'

export interface HtmlCardHostProps {
  cardId: string
  html: string
  /** Plain-text fallback for surfaces that cannot render HTML (≤120 chars). */
  fallback?: string
  className?: string
  /** Optional: send a reply into chat (e.g. wizard Finish). */
  onSubmit?: (payload: string) => void
  /** Persist card state with the parent (message annotation / local map). */
  initialState?: HtmlCardState
  onStateChange?: (state: HtmlCardState) => void
}

/** True only when the fragment is a real document root (not a mid-card `<body>` sample). */
export function isCompleteHtmlDocument(html: string): boolean {
  let trimmed = html.trim()
  while (trimmed.startsWith('<!--')) {
    const end = trimmed.indexOf('-->')
    if (end < 0) break
    trimmed = trimmed.slice(end + 3).trim()
  }
  return /^(?:<!DOCTYPE[^>]*>\s*)?<html[\s>]/i.test(trimmed)
}

/**
 * Move inline `<script>` after markup and defer until DOMContentLoaded so
 * generated cards that do `querySelectorAll(...)[i].type = …` before their
 * nodes exist do not SoftGate the whole card.
 */
/**
 * Recover tap choices when an html-card script SoftGates — pull labels from
 * static buttons/labels and from quoted string lists in the card script
 * (common for “pick what matters” suggestion cards).
 */
export function extractHtmlCardChoiceLabels(html: string): string[] {
  const labels: string[] = []
  const seen = new Set<string>()
  const push = (raw: string) => {
    const label = raw
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/\s+/g, ' ')
      .trim()
    if (!label || label.length > 90) return
    const key = label.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    labels.push(label)
  }

  for (const match of html.matchAll(
    /<(?:button|label)[^>]*>([\s\S]*?)<\/(?:button|label)>/gi
  )) {
    push(match[1] || '')
  }
  for (const match of html.matchAll(
    /\bdata-(?:reply|label|option|value)\s*=\s*["']([^"']{1,90})["']/gi
  )) {
    push(match[1] || '')
  }

  // Script-built option lists: ['Layout', "Colors", ...]
  for (const match of html.matchAll(
    /\[\s*((?:['"][^'"]{1,80}['"]\s*,\s*){1,16}['"][^'"]{1,80}['"])\s*\]/g
  )) {
    const inner = match[1] || ''
    for (const part of inner.matchAll(/['"]([^'"]{1,80})['"]/g)) {
      push(part[1] || '')
    }
  }

  // Object options: { id: 'a', label: "Charts didn't render" }
  // Match quote-delimited labels with the same closer (so apostrophes inside
  // double-quoted strings don't truncate).
  for (const match of html.matchAll(/\blabel\s*:\s*"([^"]{1,90})"/g)) {
    push(match[1] || '')
  }
  for (const match of html.matchAll(/\blabel\s*:\s*'([^']{1,90})'/g)) {
    push(match[1] || '')
  }

  return labels.slice(0, 8)
}

export function hoistAndDeferCardScripts(html: string): string {
  const scriptRe = /<script(\s[^>]*)?>([\s\S]*?)<\/script>/gi
  const deferred: string[] = []
  const markup = html.replace(scriptRe, (_full, attrs: string = '', body: string = '') => {
    if (/\bsrc\s*=/i.test(attrs || '')) return ''
    const trimmed = (body || '').trim()
    if (!trimmed) return ''
    deferred.push(`<script>
(function () {
  function __losiCardRun() {
    try {
${trimmed}
    } catch (err) {
      try {
        window.dispatchEvent(new ErrorEvent('error', {
          message: String((err && err.message) || err || 'Card script error')
        }));
      } catch (e2) {}
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', __losiCardRun);
  } else {
    __losiCardRun();
  }
})();
</script>`)
    return ''
  })

  const cleaned = markup.trim()
  if (deferred.length === 0) return cleaned
  return `${cleaned}\n${deferred.join('\n')}`
}

export function buildSrcDoc(cardId: string, html: string): string {
  const theme = resolveHtmlCardTheme()
  const baseStyles = buildHtmlCardBaseStyles(theme)
  const bridge = buildHtmlCardBridgeRuntime(cardId)
  const bridgeScript = `<script id="losi-card-bridge">${bridge}</script>`

  // Fragments may be a full document or a body snippet.
  const looksComplete = isCompleteHtmlDocument(html)
  const prepared = looksComplete ? html : hoistAndDeferCardScripts(html)
  const body = looksComplete
    ? prepared
    : `<div class="losi-card-root">${prepared}</div>`

  // The bridge MUST run before any card script: generated cards call
  // LosiCard.onState/getState synchronously at parse time, so injecting
  // the bridge at the end of <body> (after card markup + scripts) left
  // window.LosiCard undefined, the card's IIFE threw, and only its
  // static HTML painted — header visible, interactive grid empty.
  if (looksComplete) {
    // Inject theme + bridge at the start of the existing document.
    if (/<head([^>]*)>/i.test(prepared)) {
      return prepared.replace(
        /<head([^>]*)>/i,
        `<head$1><style id="losi-card-theme">${baseStyles}</style>${bridgeScript}`
      )
    }
    return prepared.replace(
      /<body([^>]*)>/i,
      `<body$1>${bridgeScript}`
    )
  }

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style id="losi-card-theme">${baseStyles}</style>
</head>
<body>
${bridgeScript}
${body}
</body>
</html>`
}

export function HtmlCardHost({
  cardId,
  html,
  fallback,
  className,
  onSubmit,
  initialState,
  onStateChange
}: HtmlCardHostProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(160)
  const [cardError, setCardError] = useState<string | null>(null)
  const [scriptFailed, setScriptFailed] = useState(false)
  const [orphanSubmit, setOrphanSubmit] = useState<string | null>(null)
  const stateRef = useRef<HtmlCardState>(initialState ?? {})
  const submittedRef = useRef(false)
  const readyRef = useRef(false)
  const safeFallback = clampHtmlCardFallback(fallback)
  const tooLarge = measureHtmlCardBytes(html) > HTML_CARD_MAX_BYTES
  const choiceLabels = useMemo(() => extractHtmlCardChoiceLabels(html), [html])

  useEffect(() => {
    submittedRef.current = false
    readyRef.current = false
    setCardError(null)
    setScriptFailed(false)
    setOrphanSubmit(null)
    stateRef.current = initialState ?? {}
  }, [cardId, html, initialState])

  const srcDoc = useMemo(() => {
    if (tooLarge) return ''
    return buildSrcDoc(cardId, html)
  }, [cardId, html, tooLarge])

  const postToCard = useCallback(
    (message: Record<string, unknown>) => {
      const frame = iframeRef.current?.contentWindow
      if (!frame) return
      frame.postMessage(
        { source: HTML_CARD_HOST_SOURCE, cardId, ...message },
        '*'
      )
    },
    [cardId]
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (!isHtmlCardToHostMessage(event.data)) return
      if (event.data.cardId !== cardId) return

      switch (event.data.type) {
        case 'ready':
          readyRef.current = true
          postToCard({ type: 'init', state: stateRef.current })
          break
        case 'error':
          // Pre-ready script failures SoftGate the interactive body; recover
          // with host-rendered suggestion links when we can recover labels.
          if (!readyRef.current) {
            setScriptFailed(true)
            setCardError(event.data.message.slice(0, 200))
          }
          break
        case 'setState':
          stateRef.current = event.data.state
          onStateChange?.(event.data.state)
          break
        case 'submit':
          if (submittedRef.current) break
          if (event.data.payload?.trim()) {
            submittedRef.current = true
            if (onSubmit) {
              onSubmit(event.data.payload.trim())
            } else {
              // Surfaces without a reply handler (shared chats, notes)
              // must not swallow the answer silently.
              setOrphanSubmit(event.data.payload.trim())
            }
          }
          break
        case 'resize':
          if (Number.isFinite(event.data.height) && event.data.height > 0) {
            setHeight(Math.min(Math.max(event.data.height, 80), 1200))
          }
          break
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [cardId, onStateChange, onSubmit, postToCard])

  // Push fresh theme CSS when the host color-scheme class flips: card
  // CSS vars are baked to absolute values in the srcDoc, so without this
  // a card mounted in light mode stays light after switching to dark.
  useEffect(() => {
    const root = document.documentElement
    const observer = new MutationObserver(() => {
      postToCard({
        type: 'theme',
        css: buildHtmlCardBaseStyles(resolveHtmlCardTheme())
      })
      postToCard({ type: 'state', state: stateRef.current })
    })
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [postToCard])

  if (tooLarge) {
    return (
      <div
        className={cn(
          'text-muted-foreground not-prose my-4 rounded-2xl bg-muted/40 px-3 py-3 text-sm',
          className
        )}
      >
        {safeFallback}
        <span className="mt-1 block text-xs opacity-70">
          Card exceeded the 1MB size limit.
        </span>
      </div>
    )
  }

  // Suggested-choice cards that crash in the iframe → native suggestion links.
  if (scriptFailed && choiceLabels.length >= 2 && onSubmit) {
    return (
      <div
        className={cn(
          'not-prose my-4 w-full rounded-2xl bg-muted/40 px-3 py-3',
          className
        )}
      >
        <div className="text-foreground text-sm font-medium">{safeFallback}</div>
        <SuggestedResponses
          suggestions={choiceLabels}
          onSend={onSubmit}
          className="mt-2"
        />
      </div>
    )
  }

  if (scriptFailed && !onSubmit) {
    return (
      <div
        className={cn(
          'text-muted-foreground not-prose my-4 rounded-2xl bg-muted/40 px-3 py-3 text-sm',
          className
        )}
      >
        {safeFallback}
        <span className="mt-1 block text-xs opacity-70">
          Reply in the composer with what you want changed.
        </span>
      </div>
    )
  }

  return (
    <div className={cn('not-prose my-4 w-full', className)}>
      <iframe
        ref={iframeRef}
        title={safeFallback}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        // No allow-same-origin — keeps the card isolated from the host.
        referrerPolicy="no-referrer"
        className="w-full rounded-2xl border-0 bg-transparent"
        style={{ height }}
      />
      {cardError && choiceLabels.length < 2 && (
        <p className="text-muted-foreground mt-2 px-1 text-xs opacity-80">
          Reply in the composer with what you want changed.
        </p>
      )}
      {orphanSubmit && (
        <p className="text-muted-foreground mt-2 rounded-xl bg-muted/40 px-3 py-2 text-xs">
          Submitted: {orphanSubmit}
        </p>
      )}
      {/* Fallback for accessibility / copy; visually hidden inside the widget surface */}
      <span className="sr-only">{safeFallback}</span>
    </div>
  )
}

/** Parse ```html-card fence metadata from the first HTML comment or leading JSON line. */
export function parseHtmlCardFence(code: string): {
  html: string
  fallback?: string
  cardId?: string
} {
  let html = code.trim()
  let fallback: string | undefined
  let cardId: string | undefined

  // Optional leading meta: <!--losi-card {"id":"...","fallback":"..."}-->
  // Match to the comment terminator (not the first '}') so fallback text
  // containing braces doesn't truncate the JSON.
  const metaMatch = html.match(
    /^<!--\s*losi-card\s+([\s\S]*?)\s*-->\s*/i
  )
  if (metaMatch) {
    try {
      const meta = JSON.parse(metaMatch[1]) as {
        id?: string
        fallback?: string
      }
      cardId = typeof meta.id === 'string' ? meta.id : undefined
      fallback =
        typeof meta.fallback === 'string' ? meta.fallback : undefined
    } catch {
      // ignore bad meta
    }
    html = html.slice(metaMatch[0].length)
  }

  return { html, fallback, cardId }
}
